import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  useTheme,
  alpha,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
  FormControl,
  TextField,
  InputAdornment,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ShareIcon from '@mui/icons-material/Share';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SortIcon from '@mui/icons-material/Sort';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import StorageIcon from '@mui/icons-material/Storage';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AppsIcon from '@mui/icons-material/Apps';
import TokenIcon from '@mui/icons-material/Token';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useConfirm } from 'material-ui-confirm';

import { useApiContext } from '@/contexts/apiContext';
import { useNotificationContext } from '@/contexts/notificationContext';
import { PipelineInstance } from '@/interfaces/pipelineInstance.interface';
import PipelinePreviewFlow from '@/components/pipelineBuilder/pipelinePreviewFlow';
import { EntityType } from '@/components/entities/entityType';
import { useUserContext } from '@/contexts/userContext';
import EntityAccessControl from '@/components/entities/administration/accessControl/accessControl';

const PAGE_SIZE = 10;

interface PipelineCardProps {
  pipeline: PipelineInstance;
  onDelete: (id: string) => Promise<void>;
  onShare: (pipeline: PipelineInstance) => void;
}

const PipelineCard = ({ pipeline, onDelete, onShare }: PipelineCardProps) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const apiContext = useApiContext();
  const { hasPermission } = useUserContext();
  const canViewShare = hasPermission('pipeline:accessControl:view', pipeline.userAccess);

  const [statsLoading, setStatsLoading] = useState<boolean>(false);
  const [totalTokens, setTotalTokens] = useState<number | null>(null);
  const [totalDsItems, setTotalDsItems] = useState<number | null>(null);
  const [processedItems, setProcessedItems] = useState<number | null>(null);
  const [failedItems, setFailedItems] = useState<number | null>(null);
  const [dataSourcesRunning, setDataSourcesRunning] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [pendingApprovals, setPendingApprovals] = useState<any[]>([]);
  const [reviewModalOpen, setReviewModalOpen] = useState<boolean>(false);
  const [reviewSubmitting, setReviewSubmitting] = useState<boolean>(false);
  const [reviewFeedback, setReviewFeedback] = useState<string>('');
  const [agentStats, setAgentStats] = useState<
    { agentName: string; total: number; processed: number; failed: number }[]
  >([]);

  const items = pipeline.items || [];
  const dataSourceCount = items.filter((item: any) => item.entityType === EntityType.dataSourceInstance).length;
  const agentCount = items.filter((item: any) => item.entityType === EntityType.agentInstance).length;
  const appCount = items.filter((item: any) => item.entityType === EntityType.userAppInstance).length;

  const formatDate = (dateInput: any) => {
    if (!dateInput) return '';
    const date = new Date(dateInput);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleResolveApproval = async (approval: any, approved: boolean) => {
    setReviewSubmitting(true);
    try {
      const stepId = approval.stepId || approval.entityId || approval.id;
      await apiContext.resumePipelineStep(pipeline.id, stepId, {
        approved,
        feedback: reviewFeedback,
        entityDefId: approval.entityDefId,
        dataSourceItemId: approval.dataSourceItemId,
        itemProcessingStatusId: approval.itemProcessingStatusId,
      });
      // Refresh pending approvals
      const updated = await apiContext.getPendingApprovals(pipeline.id);
      setPendingApprovals(Array.isArray(updated) ? updated : []);
      if (!updated || updated.length === 0) {
        setReviewModalOpen(false);
      }
    } catch (err) {
      console.error('Failed to resolve approval:', err);
    } finally {
      setReviewSubmitting(false);
    }
  };

  useEffect(() => {
    let active = true;
    const loadLazyStats = async (isSilent = false) => {
      const dataSources = items.filter((item: any) => item.entityType === EntityType.dataSourceInstance);
      const agents = items.filter((item: any) => item.entityType === EntityType.agentInstance);
      const apps = items.filter((item: any) => item.entityType === EntityType.userAppInstance);

      if (dataSources.length === 0 && agents.length === 0 && apps.length === 0) {
        return;
      }

      if (!isSilent) {
        setStatsLoading(true);
      }

      try {
        const approvalsPromise = apiContext.getPendingApprovals(pipeline.id).catch((e) => {
          console.warn(`Failed to fetch pending approvals for pipeline ${pipeline.id}:`, e);
          return [];
        });
        const dsPromises = dataSources.map(async (ds: any) => {
          try {
            const status = await apiContext.getDataSourceInstanceProcessingStatus(ds.dataSourceDefId, ds.id);
            return !!(status && status.runStartTime && !status.runEndTime);
          } catch (e) {
            console.error(`Failed to fetch run status for ds ${ds.id}`, e);
            return false;
          }
        });

        const dsStatsPromises = dataSources.map(async (ds: any) => {
          try {
            const stats = await apiContext.getDataSourceInstanceUsageStats(ds.dataSourceDefId, ds.id);
            return Number(stats?.dataSourceItemStats?.total) || 0;
          } catch (e) {
            console.error(`Failed to fetch usage stats for ds ${ds.id}`, e);
            return 0;
          }
        });

        const agentItemsPromises = agents.map(async (agent: any) => {
          try {
            const statuses = await apiContext.getAgentInstanceItemProcessingStatuses(
              agent.agentDefId,
              agent.id,
              pipeline.id,
            );
            const list = Array.isArray(statuses) ? statuses : [];
            return {
              agentName: agent.name || 'Agent',
              total: list.length,
              processed: list.filter((s: any) => s.status === 'PROCESSED').length,
              failed: list.filter((s: any) => s.status === 'FAILED').length,
            };
          } catch (e) {
            console.error(`Failed to fetch processing statuses for agent ${agent.id}`, e);
            return { agentName: agent.name || 'Agent', total: 0, processed: 0, failed: 0 };
          }
        });

        const agentLlmPromises = agents.map(async (agent: any) => {
          try {
            const usages = await apiContext.getEntityLlmUsages(agent.entityType, agent.agentDefId, agent.id);
            if (Array.isArray(usages)) {
              return usages.reduce((sum: number, u: any) => sum + (Number(u.totalTokens) || 0), 0);
            }
            return 0;
          } catch (e) {
            console.error(`Failed to fetch llm usages for agent ${agent.id}`, e);
            return 0;
          }
        });

        const appLlmPromises = apps.map(async (app: any) => {
          try {
            const usages = await apiContext.getEntityLlmUsages(app.entityType, app.userAppDefId, app.id);
            if (Array.isArray(usages)) {
              return usages.reduce((sum: number, u: any) => sum + (Number(u.totalTokens) || 0), 0);
            }
            return 0;
          } catch (e) {
            console.error(`Failed to fetch llm usages for app ${app.id}`, e);
            return 0;
          }
        });

        const [dsResults, dsStatsResults, agentItemsResults, agentLlmResults, appLlmResults, pendingApprovalsResult] =
          await Promise.all([
            Promise.all(dsPromises),
            Promise.all(dsStatsPromises),
            Promise.all(agentItemsPromises),
            Promise.all(agentLlmPromises),
            Promise.all(appLlmPromises),
            approvalsPromise,
          ]);

        if (!active) return;

        const isAnyDsRunning = dsResults.some((isRunning) => isRunning);
        const totalDsCount = dsStatsResults.reduce((sum, count) => sum + count, 0);
        const totalItemsCount = agentItemsResults.reduce((sum, item) => sum + item.total, 0);
        const processedItemsCount = agentItemsResults.reduce((sum, item) => sum + item.processed, 0);
        const failedItemsCount = agentItemsResults.reduce((sum, item) => sum + item.failed, 0);
        const totalTokensCount = [...agentLlmResults, ...appLlmResults].reduce((sum, count) => sum + count, 0);

        setDataSourcesRunning(isAnyDsRunning);
        setTotalDsItems(totalDsCount);
        setProcessedItems(processedItemsCount);
        setFailedItems(failedItemsCount);
        setTotalTokens(totalTokensCount);
        setAgentStats(agentItemsResults);
        setPendingApprovals(Array.isArray(pendingApprovalsResult) ? pendingApprovalsResult : []);
      } catch (err) {
        console.error('Failed to load pipeline stats', err);
      } finally {
        if (active && !isSilent) {
          setStatsLoading(false);
        }
      }
    };

    loadLazyStats(false);

    const interval = setInterval(() => {
      loadLazyStats(true);
    }, 10000); // Refresh stats every 10 seconds

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [pipeline.id, pipeline.items]);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(pipeline.id);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Card
      className="glass-card-light"
      sx={{
        bgcolor: 'rgba(255, 255, 255, 0.4)',
        borderColor: 'divider',
        transition: 'all 0.3s ease',
        boxShadow: 'none',
        borderRadius: '16px',
        borderWidth: 1,
        opacity: isDeleting ? 0.6 : 1,
        '&:hover': {
          boxShadow: `0 8px 24px ${alpha(theme.palette.secondary.main, 0.08)}`,
          borderColor: theme.palette.secondary.main,
        },
      }}
    >
      <CardContent sx={{ p: 2, pb: '12px !important' }}>
        {/* Header row */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5} sx={{ mb: 1.5 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25, flexWrap: 'wrap' }}>
              <Tooltip title={pipeline.name} placement="top-start">
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 850,
                    color: 'text.primary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'default',
                    maxWidth: '100%',
                  }}
                >
                  {pipeline.name}
                </Typography>
              </Tooltip>
              {pipeline.isSample && (
                <Chip
                  icon={
                    <AutoAwesomeIcon
                      sx={{
                        fontSize: '10px !important',
                        color: `${alpha(theme.palette.warning.main, 0.9)} !important`,
                      }}
                    />
                  }
                  label="Sample"
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '9px',
                    fontWeight: 800,
                    letterSpacing: '0.4px',
                    px: 0.25,
                    bgcolor: alpha(theme.palette.warning.main, 0.12),
                    color: theme.palette.warning.dark,
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                    '& .MuiChip-label': { px: 0.5 },
                  }}
                />
              )}
              {pendingApprovals.length > 0 && (
                <Chip
                  icon={
                    <WarningAmberIcon
                      sx={{
                        fontSize: '11px !important',
                        color: '#f59e0b !important',
                        animation: 'pulse 1.5s infinite',
                        '@keyframes pulse': {
                          '0%': { transform: 'scale(0.95)', opacity: 0.8 },
                          '50%': { transform: 'scale(1.15)', opacity: 1 },
                          '100%': { transform: 'scale(0.95)', opacity: 0.8 },
                        },
                      }}
                    />
                  }
                  label={`${pendingApprovals.length} Approval${pendingApprovals.length > 1 ? 's' : ''} Pending`}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setReviewModalOpen(true);
                  }}
                  clickable
                  sx={{
                    height: 18,
                    fontSize: '9px',
                    fontWeight: 800,
                    letterSpacing: '0.3px',
                    px: 0.25,
                    bgcolor: '#fef3c7',
                    color: '#b45309',
                    border: '1px solid #fcd34d',
                    cursor: 'pointer',
                    '& .MuiChip-label': { px: 0.5 },
                    '&:hover': {
                      bgcolor: '#fde68a',
                    },
                  }}
                />
              )}
            </Stack>
            <Tooltip title={pipeline.description || 'No description provided.'} placement="bottom-start">
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.3,
                  cursor: 'default',
                }}
              >
                {pipeline.description || 'No description provided.'}
              </Typography>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
            <Tooltip title="Open in Builder">
              <span>
                <RouterLink
                  to={isDeleting ? '#' : `/pipelineBuilder?pipelineInstanceId=${pipeline.id}`}
                  style={{ textDecoration: 'none', pointerEvents: isDeleting ? 'none' : 'auto' }}
                >
                  <IconButton
                    size="small"
                    disabled={isDeleting}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      color: theme.palette.primary.main,
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) },
                      '&.Mui-disabled': { opacity: 0.5 },
                    }}
                  >
                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </RouterLink>
              </span>
            </Tooltip>
            {canViewShare && (
              <Tooltip title="Share Pipeline">
                <IconButton
                  size="small"
                  disabled={isDeleting}
                  onClick={() => onShare(pipeline)}
                  sx={{
                    bgcolor: alpha(theme.palette.secondary.main, 0.08),
                    color: theme.palette.secondary.main,
                    '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.15) },
                  }}
                >
                  <ShareIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={isDeleting ? 'Deleting...' : 'Delete Pipeline'}>
              <IconButton
                size="small"
                disabled={isDeleting}
                onClick={handleDelete}
                sx={{
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                  color: theme.palette.error.main,
                  '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.15) },
                }}
              >
                {isDeleting ? <CircularProgress size={16} color="error" /> : <DeleteIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>

        {/* Inline ReactFlow preview */}
        <PipelinePreviewFlow pipeline={pipeline} />

        {/* Stats bar */}
        <Box
          sx={{
            mt: 1.5,
            mb: 0.75,
            p: 0.75,
            borderRadius: '10px',
            bgcolor: 'rgba(240, 244, 248, 0.35)',
            border: '1px solid rgba(255, 255, 255, 0.4)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
          }}
        >
          {/* Left Section: Entity Count Summary */}
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {dataSourceCount > 0 && (
              <Chip
                size="small"
                icon={<StorageIcon sx={{ fontSize: '11px !important', color: '#10b981 !important' }} />}
                label={`${dataSourceCount} Data Source${dataSourceCount > 1 ? 's' : ''}`}
                sx={{
                  height: 20,
                  fontSize: '9.5px',
                  fontWeight: 750,
                  bgcolor: alpha('#10b981', 0.08),
                  border: '1px solid',
                  borderColor: alpha('#10b981', 0.25),
                  color: '#047857',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
            {agentCount > 0 && (
              <Chip
                size="small"
                icon={<PsychologyIcon sx={{ fontSize: '11px !important', color: '#8b5cf6 !important' }} />}
                label={`${agentCount} Agent${agentCount > 1 ? 's' : ''}`}
                sx={{
                  height: 20,
                  fontSize: '9.5px',
                  fontWeight: 750,
                  bgcolor: alpha('#8b5cf6', 0.08),
                  border: '1px solid',
                  borderColor: alpha('#8b5cf6', 0.25),
                  color: '#6d28d9',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
            {appCount > 0 && (
              <Chip
                size="small"
                icon={<AppsIcon sx={{ fontSize: '11px !important', color: '#3b82f6 !important' }} />}
                label={`${appCount} App${appCount > 1 ? 's' : ''}`}
                sx={{
                  height: 20,
                  fontSize: '9.5px',
                  fontWeight: 750,
                  bgcolor: alpha('#3b82f6', 0.08),
                  border: '1px solid',
                  borderColor: alpha('#3b82f6', 0.25),
                  color: '#1d4ed8',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
          </Stack>

          {/* Right Section: Aggregated Metrics */}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
            {statsLoading ? (
              <CircularProgress size={10} color="secondary" />
            ) : (
              <>
                {totalDsItems !== null && dataSourceCount > 0 && (
                  <Tooltip title={`${totalDsItems} total documents/items ingested`}>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{
                        px: 0.75,
                        py: 0.25,
                        borderRadius: '6px',
                        bgcolor: alpha(theme.palette.info.main, 0.08),
                        color: theme.palette.info.dark,
                        border: '1px solid',
                        borderColor: alpha(theme.palette.info.main, 0.2),
                      }}
                    >
                      <InsertDriveFileIcon sx={{ fontSize: 11 }} />
                      <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '9px' }}>
                        {totalDsItems} ingested
                      </Typography>
                    </Stack>
                  </Tooltip>
                )}

                {processedItems !== null && agentCount > 0 && (
                  <Tooltip
                    title={
                      <Box sx={{ p: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 0.5 }}>
                          Processed Items Breakdown:
                        </Typography>
                        {agentStats.map((as, idx) => (
                          <Typography key={idx} variant="caption" sx={{ display: 'block', fontSize: '11px' }}>
                            • {as.agentName}: {as.processed} processed
                          </Typography>
                        ))}
                      </Box>
                    }
                  >
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{
                        px: 0.75,
                        py: 0.25,
                        borderRadius: '6px',
                        bgcolor: alpha(theme.palette.success.main, 0.08),
                        color: theme.palette.success.dark,
                        border: '1px solid',
                        borderColor: alpha(theme.palette.success.main, 0.2),
                      }}
                    >
                      <CheckCircleOutlineIcon sx={{ fontSize: 11 }} />
                      <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '9px' }}>
                        {processedItems} processed
                      </Typography>
                    </Stack>
                  </Tooltip>
                )}

                {failedItems !== null && agentCount > 0 && (
                  <Tooltip
                    title={
                      <Box sx={{ p: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 0.5 }}>
                          Failed Items Breakdown:
                        </Typography>
                        {agentStats.map((as, idx) => (
                          <Typography key={idx} variant="caption" sx={{ display: 'block', fontSize: '11px' }}>
                            • {as.agentName}: {as.failed} failed
                          </Typography>
                        ))}
                      </Box>
                    }
                  >
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{
                        px: 0.75,
                        py: 0.25,
                        borderRadius: '6px',
                        bgcolor: alpha(theme.palette.error.main, 0.08),
                        color: theme.palette.error.dark,
                        border: '1px solid',
                        borderColor: alpha(theme.palette.error.main, 0.2),
                      }}
                    >
                      <ErrorOutlineIcon sx={{ fontSize: 11 }} />
                      <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '9px' }}>
                        {failedItems} failed
                      </Typography>
                    </Stack>
                  </Tooltip>
                )}

                {totalTokens !== null && (agentCount > 0 || appCount > 0) && (
                  <Tooltip title={`${totalTokens.toLocaleString()} LLM tokens consumed`}>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{
                        px: 0.75,
                        py: 0.25,
                        borderRadius: '6px',
                        bgcolor: alpha(theme.palette.secondary.main, 0.08),
                        color: theme.palette.secondary.dark,
                        border: '1px solid',
                        borderColor: alpha(theme.palette.secondary.main, 0.2),
                      }}
                    >
                      <TokenIcon sx={{ fontSize: 11 }} />
                      <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '9px' }}>
                        {totalTokens > 1000000
                          ? `${(totalTokens / 1000000).toFixed(1)}M`
                          : totalTokens > 1000
                            ? `${(totalTokens / 1000).toFixed(1)}k`
                            : totalTokens}{' '}
                        tokens
                      </Typography>
                    </Stack>
                  </Tooltip>
                )}
              </>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 1, opacity: 0.5 }} />

        {/* Footer meta */}
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
              <ScheduleIcon sx={{ fontSize: 12 }} />
              <Typography variant="caption" sx={{ fontSize: '9.5px', fontWeight: 600 }}>
                {formatDate(pipeline.createdDate)}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
              <PersonOutlineIcon sx={{ fontSize: 12 }} />
              <Typography
                variant="caption"
                sx={{
                  fontSize: '9.5px',
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 160,
                }}
              >
                {pipeline.createdBy}
              </Typography>
            </Stack>
          </Stack>

          {pendingApprovals.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={(e) => {
                e.stopPropagation();
                setReviewModalOpen(true);
              }}
              startIcon={<WarningAmberIcon sx={{ fontSize: '13px !important' }} />}
              sx={{
                height: 22,
                fontSize: '10px',
                fontWeight: 750,
                textTransform: 'none',
                px: 1,
                py: 0,
                borderRadius: '6px',
                borderColor: '#f59e0b',
                color: '#b45309',
                bgcolor: '#fffbeb',
                '&:hover': {
                  bgcolor: '#fef3c7',
                  borderColor: '#d97706',
                },
              }}
            >
              Review Approvals ({pendingApprovals.length})
            </Button>
          )}
        </Stack>
      </CardContent>

      {/* Review Approvals Dialog */}
      <Dialog
        open={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px', p: 1 },
        }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WarningAmberIcon sx={{ color: '#f59e0b' }} />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Pending Approvals — {pipeline.name}
            </Typography>
          </Stack>
          <IconButton size="small" onClick={() => setReviewModalOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            The following autonomous agent action(s) require human confirmation before execution can continue.
          </Typography>

          <Stack spacing={2}>
            {pendingApprovals.map((approval, aIdx) => {
              const actionTitle =
                approval.action ||
                approval.approvalRequest?.action ||
                approval.proposedAction?.action ||
                'High-Consequence Tool Action';
              const params =
                approval.parameters ||
                approval.approvalRequest?.parameters ||
                approval.proposedAction?.parameters ||
                approval.proposedAction?.args ||
                {};
              const rationale =
                approval.prompt ||
                approval.approvalRequest?.prompt ||
                approval.reason ||
                'Agent paused execution awaiting approval.';

              return (
                <Card
                  key={approval.id || aIdx}
                  variant="outlined"
                  sx={{ p: 2, borderRadius: '12px', border: '1px solid #fcd34d', bgcolor: '#fffdf5' }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 750, color: '#92400e' }}>
                      {actionTitle}
                    </Typography>
                    <Chip
                      size="small"
                      label={approval.stepName || approval.entityDefId || `Step #${approval.stepIndex ?? aIdx + 1}`}
                      sx={{ fontSize: '10px', height: 20, bgcolor: '#fef3c7', color: '#b45309' }}
                    />
                  </Stack>

                  <Typography variant="caption" sx={{ color: '#78350f', display: 'block', mb: 1 }}>
                    {rationale}
                  </Typography>

                  {Object.keys(params).length > 0 && (
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: '8px',
                        bgcolor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        overflowX: 'auto',
                        mb: 1.5,
                      }}
                    >
                      <pre style={{ margin: 0 }}>{JSON.stringify(params, null, 2)}</pre>
                    </Box>
                  )}

                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Optional human feedback or guidance for the agent..."
                    value={reviewFeedback}
                    onChange={(e) => setReviewFeedback(e.target.value)}
                    sx={{ mb: 1.5, bgcolor: '#ffffff' }}
                  />

                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      disabled={reviewSubmitting}
                      onClick={() => handleResolveApproval(approval, false)}
                      sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      Reject
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      disabled={reviewSubmitting}
                      onClick={() => handleResolveApproval(approval, true)}
                      sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      {reviewSubmitting ? <CircularProgress size={16} color="inherit" /> : 'Approve & Resume'}
                    </Button>
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

type Props = {
  maxHeight: number;
  onPipelineDeleted?: () => void;
};

const PipelinesPage = ({ maxHeight, onPipelineDeleted }: Props) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const apiContext = useApiContext();
  const { toast } = useNotificationContext();
  const confirm = useConfirm();
  const { user } = useUserContext();

  const [loading, setLoading] = useState<boolean>(true);
  const [pipelines, setPipelines] = useState<PipelineInstance[]>([]);
  const [page, setPage] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState<PipelineInstance | null>(null);

  const [sortBy, setSortBy] = useState<string>('lastUpdatedDate');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const isNewUser = useMemo(() => {
    if (!user || !user.createdDate) return false;
    const createdTime = new Date(user.createdDate).getTime();
    return Date.now() - createdTime < 120000;
  }, [user]);

  useEffect(() => {
    if (!isNewUser || pipelines.length > 0) return;

    const interval = setInterval(() => {
      apiContext
        .getPipelineInstances(sortBy, sortOrder)
        .then((data: any) => {
          const fetched = Array.isArray(data) ? data : data?.data || [];
          if (fetched.length > 0) {
            setPipelines(fetched);
            setPage(0);
            if (onPipelineDeleted) {
              onPipelineDeleted();
            }
          }
        })
        .catch((err) => console.error('Silent polling pipelines failed', err));
    }, 3000);

    return () => clearInterval(interval);
  }, [isNewUser, pipelines.length, sortBy, sortOrder, apiContext, onPipelineDeleted]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const filteredPipelines = useMemo(() => {
    if (!searchQuery.trim()) {
      return pipelines;
    }
    const query = searchQuery.toLowerCase().trim();
    return pipelines.filter((pipeline) => {
      const nameMatch = pipeline.name?.toLowerCase().includes(query);
      const descMatch = pipeline.description?.toLowerCase().includes(query);
      const itemsMatch = pipeline.items?.some((item: any) => {
        const itemNameMatch = item.name?.toLowerCase().includes(query);
        const itemDescMatch = item.description?.toLowerCase().includes(query);
        return itemNameMatch || itemDescMatch;
      });
      return nameMatch || descMatch || itemsMatch;
    });
  }, [pipelines, searchQuery]);

  const handleSharePipeline = (pipeline: PipelineInstance) => {
    setSelectedPipeline(pipeline);
    setShareOpen(true);
  };

  useEffect(() => {
    fetchPipelines(sortBy, sortOrder);
  }, [sortBy, sortOrder]);

  const fetchPipelines = async (activeSortBy = sortBy, activeSortOrder = sortOrder) => {
    try {
      setLoading(true);
      const data = (await apiContext.getPipelineInstances(activeSortBy, activeSortOrder)) as any;
      setPipelines(Array.isArray(data) ? data : data?.data || []);
      setPage(0);
    } catch (err) {
      console.error('Failed to fetch pipelines', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePipeline = async (id: string) => {
    return new Promise<void>((resolve, reject) => {
      confirm({
        description: 'Are you sure you want to delete this pipeline instance? This will remove all connections.',
        confirmationText: 'Delete',
        cancellationText: 'Cancel',
        title: 'Delete Pipeline',
      })
        .then(async () => {
          try {
            await apiContext.deletePipelineInstance(id, true);
            toast({ message: 'Pipeline deleted successfully' });
            fetchPipelines();
            if (onPipelineDeleted) {
              onPipelineDeleted();
            }
            resolve();
          } catch (err: any) {
            console.error('Failed to delete pipeline', err);
            const errMsg = err?.message || 'Failed to delete pipeline';
            toast({ message: errMsg, type: 'error' });
            reject(err);
          }
        })
        .catch(() => {
          resolve();
        });
    });
  };

  const formatDate = (dateInput: any) => {
    if (!dateInput) return '';
    const date = new Date(dateInput);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading && pipelines.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: maxHeight }}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (pipelines.length === 0) {
    if (isNewUser) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: maxHeight,
            p: 3,
            textAlign: 'center',
          }}
        >
          <CircularProgress color="secondary" size={32} sx={{ mb: 2 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1, color: 'text.primary' }}>
            Setting up your workspace...
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 360 }}>
            We are provisioning your demo pipelines and sample files. This will take just a few seconds.
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: maxHeight,
          p: 3,
          textAlign: 'center',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1, color: 'text.primary' }}>
          No active pipelines
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5, maxWidth: 380 }}>
          Create a visual workflow connecting your data sources, processors, and applications using the Pipeline
          Builder.
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigate('/buildOptions')}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 700,
            color: 'text.primary',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          Create Pipeline
        </Button>
      </Box>
    );
  }

  const totalPages = Math.ceil(filteredPipelines.length / PAGE_SIZE);
  const paginated = filteredPipelines.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <Box sx={{ height: maxHeight, display: 'flex', flexDirection: 'column' }}>
      {/* Sort Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.background.paper, 0.1),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%', flexWrap: 'wrap', gap: 1 }}>
          <TextField
            placeholder="Search pipelines or entities..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              width: { xs: '100%', sm: 260 },
              '& .MuiInputBase-root': {
                height: 28,
                fontSize: '11px',
                borderRadius: '8px',
                bgcolor: 'rgba(255, 255, 255, 0.5)',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ p: 0.25 }}>
                    <CloseIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ flexGrow: 1 }} />

          <SortIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 750,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              fontSize: '9px',
            }}
          >
            Sort By
          </Typography>

          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as string)}
            variant="standard"
            disableUnderline
            sx={{
              fontSize: '11px',
              fontWeight: 700,
              color: theme.palette.secondary.main,
              cursor: 'pointer',
              '& .MuiSelect-select': {
                py: 0.2,
                pr: '20px !important',
              },
            }}
          >
            <MenuItem value="lastUpdatedDate" sx={{ fontSize: '11px', fontWeight: 700 }}>
              Last Updated
            </MenuItem>
            <MenuItem value="createdDate" sx={{ fontSize: '11px', fontWeight: 700 }}>
              Created Date
            </MenuItem>
            <MenuItem value="name" sx={{ fontSize: '11px', fontWeight: 700 }}>
              Name
            </MenuItem>
          </Select>

          <Tooltip title={sortOrder === 'ASC' ? 'Ascending' : 'Descending'}>
            <IconButton
              size="small"
              onClick={() => setSortOrder((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'))}
              sx={{
                width: 22,
                height: 22,
                borderRadius: '6px',
                bgcolor: alpha(theme.palette.secondary.main, 0.06),
                color: theme.palette.secondary.main,
                '&:hover': {
                  bgcolor: alpha(theme.palette.secondary.main, 0.12),
                },
              }}
            >
              {sortOrder === 'ASC' ? (
                <ArrowUpwardIcon sx={{ fontSize: 13 }} />
              ) : (
                <ArrowDownwardIcon sx={{ fontSize: 13 }} />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={() => fetchPipelines(sortBy, sortOrder)}
              sx={{
                width: 22,
                height: 22,
                borderRadius: '6px',
                bgcolor: alpha(theme.palette.secondary.main, 0.06),
                color: theme.palette.secondary.main,
                '&:hover': {
                  bgcolor: alpha(theme.palette.secondary.main, 0.12),
                },
              }}
            >
              <RefreshIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Scrollable pipeline list */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2, pb: 1 }}>
        <Grid container spacing={2}>
          {paginated.map((pipeline) => (
            <Grid size={{ xs: 12 }} key={pipeline.id}>
              <PipelineCard pipeline={pipeline} onDelete={handleDeletePipeline} onShare={handleSharePipeline} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Pagination bar — only shown when there's more than one page */}
      {totalPages > 1 && (
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'rgba(255,255,255,0.6)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filteredPipelines.length)} of{' '}
            {filteredPipelines.length}
          </Typography>

          <Stack direction="row" spacing={0.5} alignItems="center">
            <IconButton
              size="small"
              onClick={() => setPage((p) => p - 1)}
              disabled={page === 0}
              sx={{
                bgcolor: alpha(theme.palette.secondary.main, 0.07),
                '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.14) },
                '&.Mui-disabled': { opacity: 0.35 },
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            </IconButton>

            {/* Page number pills */}
            {Array.from({ length: totalPages }, (_, i) => (
              <Box
                key={i}
                onClick={() => setPage(i)}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  transition: 'all 0.2s',
                  bgcolor: i === page ? theme.palette.secondary.main : alpha(theme.palette.secondary.main, 0.07),
                  color: i === page ? 'white' : 'text.secondary',
                  '&:hover': {
                    bgcolor: i === page ? theme.palette.secondary.main : alpha(theme.palette.secondary.main, 0.15),
                  },
                }}
              >
                {i + 1}
              </Box>
            ))}

            <IconButton
              size="small"
              onClick={() => setPage((p) => p + 1)}
              disabled={page === totalPages - 1}
              sx={{
                bgcolor: alpha(theme.palette.secondary.main, 0.07),
                '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.14) },
                '&.Mui-disabled': { opacity: 0.35 },
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </Box>
      )}

      <Dialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            backgroundImage: 'none',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1, color: 'text.primary' }}>
          Share Pipeline: {selectedPipeline?.name}
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {selectedPipeline && (
            <EntityAccessControl
              entity={{
                ...selectedPipeline,
                entityType: 'pipelineInstance',
                entityDefId: '',
              }}
              maxHeight={400}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PipelinesPage;
